import React, { useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, TestTube } from "@phosphor-icons/react";
import api from "@/lib/api";
import { AdminModal, BTN_BLACK, BTN_WHITE, INPUT } from "@/components/pageSettings/AdminModal";
import { CASE_TYPES, StateOptions, TestLeadResult } from "@/components/SendTestLeadButton";

const where = (d) => `${d.name} · ${d.type}${d.page ? ` · page ${d.page}` : ""}${d.extra_places ? ` · +${d.extra_places} extra place${d.extra_places === 1 ? "" : "s"}` : ""}`;

function TestForm({ opts, f, setF }) {
  const dest = opts.deliveries.find((d) => d.id === f.dest) || opts.deliveries[0];
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="grid gap-3 sm:grid-cols-3 text-xs">
      <label className="sm:col-span-3"><span className="label-mono">Delivery</span>
        <select className={INPUT} value={f.dest} onChange={(e) => setF({ ...f, dest: e.target.value, state: "" })} data-testid="test-buyer-dest">
          {opts.deliveries.map((d) => <option key={d.id} value={d.id}>{where(d)}</option>)}
        </select>
      </label>
      <label><span className="label-mono">Visitor&apos;s state</span>
        <select className={INPUT} value={f.state} onChange={set("state")} data-testid="test-buyer-state">
          <option value="">{dest?.states?.length ? `One they take (${dest.states[0]})` : "Picked for you"}</option>
          <StateOptions served={dest?.states} />
        </select>
      </label>
      <label><span className="label-mono">Case type</span>
        <select className={INPUT} value={f.case_type} onChange={set("case_type")} data-testid="test-buyer-case">
          <option value="">One they take</option>
          {CASE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
    </div>
  );
}

// Client page: send one sample TEST lead the way this buyer gets real leads and show what each place received.
export function TestBuyerButton({ orgId, firm }) {
  const [opts, setOpts] = useState(null);
  const [f, setF] = useState({ dest: "", state: "", case_type: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const open = async () => {
    try {
      const { data } = await api.get(`/clients/${orgId}/test-buyer`);
      setOpts(data);
      setF({ dest: data.deliveries[0]?.id || "", state: "", case_type: "" });
      setResult(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open the test");
    }
  };
  const send = async () => {
    setBusy(true);
    try {
      const body = { destination_id: f.dest, ...(f.state ? { state: f.state } : {}), ...(f.case_type ? { case_type: f.case_type } : {}) };
      const { data } = await api.post(`/clients/${orgId}/test-buyer`, body);
      setResult(data);
      const att = data.attempts || [];
      const ok = att.filter((a) => a.status === "delivered").length;
      (att.length && ok === att.length ? toast.success : toast.warning)(`Test sent: ${ok} of ${att.length} place${att.length === 1 ? "" : "s"} got it`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the test");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" onClick={open} data-testid="test-buyer-btn"
        className="border-2 border-yellow-500 bg-yellow-400 px-3 py-2 text-[10px] uppercase tracking-widest text-black hover:bg-yellow-300 inline-flex items-center gap-1">
        <TestTube size={12} weight="bold" /> Test this buyer
      </button>
      {opts && (
        <AdminModal eyebrow="Test this buyer" title={firm} onClose={() => setOpts(null)} testId="test-buyer-modal" wide
          footer={opts.deliveries.length > 0 && (
            <div className="flex justify-end gap-2">
              <button type="button" className={BTN_WHITE} onClick={() => setOpts(null)} data-testid="test-buyer-close">Close</button>
              <button type="button" className={BTN_BLACK} disabled={busy} onClick={send} data-testid="test-buyer-send">
                <PaperPlaneTilt size={11} weight="bold" /> {busy ? "Sending…" : result ? "Send another" : "Send sample test lead"}
              </button>
            </div>
          )}>
          {!opts.deliveries.length ? (
            <p className="text-xs" data-testid="test-buyer-no-delivery">This buyer has no active delivery yet. Add one first.</p>
          ) : (
            <div className="space-y-3">
              <p className="text-[11px] text-zinc-600 leading-relaxed">
                Sends one made-up TEST lead the way this buyer gets real ones: their lead rules, their fields and formats,
                their email or web post, and every extra place. It never counts toward their spend, lead count, caps or
                invoices, and isn&apos;t retried.
              </p>
              <TestForm opts={opts} f={f} setF={setF} />
              {result && <TestLeadResult r={result} showLeadLink testId="test-buyer" />}
            </div>
          )}
        </AdminModal>
      )}
    </>
  );
}
